import React, { useState } from 'react';
import useSWR from 'swr';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  createThresholdSchedule, deleteThresholdSchedule, listThresholdSchedule, patchThresholdSchedule,
  type ThresholdScheduleDto, type ThresholdScheduleWrite,
} from '@/api/thresholdSchedule';
import { Tooltip } from '@/components/ui/tooltip';

/** Редактор расписания вечернего понижения порога (Алексей 14.10, #1556/#1559–#1561, #1566
 *  «в админку то я не хожу»). Строка: тип стеллажа → «с часа» ИЛИ «за N ч до закрытия» →
 *  множитель порога; область — все филиалы / этот филиал / стеллаж. Приоритет стеллаж > филиал >
 *  общее; из подходящих активна та, чей старт позже. Применяется и в алертах, и в отчёте дня. */

const RACK_TYPES = ['Выпечка', 'Готовая еда', 'Десерты', 'Сопутствующая продукция', 'Напитки', 'Кофе'];

type Draft = {
  rack_type: string;
  mode: 'hour' | 'before_close';
  hour: string;
  before: string;
  multiplier: string;
  scope: 'all' | 'zone' | 'rack';
  rack: string;
};

const EMPTY: Draft = { rack_type: 'Выпечка', mode: 'hour', hour: '16', before: '2', multiplier: '0', scope: 'all', rack: '' };

function toDraft(r: ThresholdScheduleDto): Draft {
  return {
    rack_type: r.rack_type,
    mode: r.from_hour != null ? 'hour' : 'before_close',
    hour: r.from_hour != null ? String(r.from_hour) : '16',
    before: r.hours_before_close != null ? String(r.hours_before_close) : '2',
    multiplier: String(r.multiplier),
    scope: r.rack != null ? 'rack' : r.zone != null ? 'zone' : 'all',
    rack: r.rack != null ? String(r.rack) : '',
  };
}

function toWrite(d: Draft, zoneId: string): ThresholdScheduleWrite | string {
  const m = Number(d.multiplier);
  if (!Number.isFinite(m) || m < 0 || m > 1) return 'Множитель — от 0 до 1';
  const hour = d.mode === 'hour' ? Number(d.hour) : null;
  const before = d.mode === 'before_close' ? Number(d.before) : null;
  if (d.mode === 'hour' && (!Number.isInteger(hour) || hour! < 0 || hour! > 23)) return '«С часа» — 0–23';
  if (d.mode === 'before_close' && (!Number.isFinite(before) || before! <= 0)) return '«За N ч до закрытия» — больше 0';
  if (d.scope === 'rack' && !d.rack) return 'Выбери стеллаж';
  return {
    rack_type: d.rack_type.trim(),
    from_hour: hour,
    hours_before_close: before,
    multiplier: m,
    zone: d.scope === 'zone' ? Number(zoneId) : null,
    rack: d.scope === 'rack' ? Number(d.rack) : null,
    enabled: true,
    comment: '',
  };
}

function scopeLabel(r: ThresholdScheduleDto): string {
  if (r.rack != null) return `стеллаж «${r.rack_name ?? r.rack}»`;
  if (r.zone != null) return `филиал ${r.zone_name ?? r.zone}`;
  return 'все филиалы';
}

function whenLabel(r: ThresholdScheduleDto): string {
  return r.from_hour != null ? `с ${String(r.from_hour).padStart(2, '0')}:00` : `за ${r.hours_before_close} ч до закрытия`;
}

function RowForm({ draft, setDraft, racks, onSave, onCancel, saving, error }: {
  draft: Draft; setDraft: (d: Draft) => void; racks: { id: string; name: string }[];
  onSave: () => void; onCancel: () => void; saving: boolean; error: string | null;
}) {
  const set = (k: keyof Draft, v: string) => setDraft({ ...draft, [k]: v });
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
      <label className="flex flex-col gap-0.5">Тип стеллажа
        <Input list="ts-rack-types" value={draft.rack_type} onChange={(e) => set('rack_type', e.target.value)} className="h-7 w-44 text-xs" placeholder="пусто = любой" />
        <datalist id="ts-rack-types">{RACK_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
      </label>
      <label className="flex flex-col gap-0.5">Когда
        <Select value={draft.mode} onChange={(e) => set('mode', e.target.value)} className="h-7 text-xs">
          <option value="hour">с часа (местное время)</option>
          <option value="before_close">за N ч до закрытия</option>
        </Select>
      </label>
      {draft.mode === 'hour'
        ? <label className="flex flex-col gap-0.5">Час<Input type="number" min={0} max={23} value={draft.hour} onChange={(e) => set('hour', e.target.value)} className="h-7 w-16 text-xs" /></label>
        : <label className="flex flex-col gap-0.5">Часов до закрытия<Input type="number" min={0.5} step={0.5} value={draft.before} onChange={(e) => set('before', e.target.value)} className="h-7 w-20 text-xs" /></label>}
      <label className="flex flex-col gap-0.5">Множитель порога<Input type="number" min={0} max={1} step={0.05} value={draft.multiplier} onChange={(e) => set('multiplier', e.target.value)} className="h-7 w-20 text-xs" /></label>
      <label className="flex flex-col gap-0.5">Область
        <Select value={draft.scope} onChange={(e) => set('scope', e.target.value)} className="h-7 text-xs">
          <option value="all">все филиалы</option>
          <option value="zone">этот филиал</option>
          <option value="rack">стеллаж</option>
        </Select>
      </label>
      {draft.scope === 'rack' && (
        <label className="flex flex-col gap-0.5">Стеллаж
          <Select value={draft.rack} onChange={(e) => set('rack', e.target.value)} className="h-7 text-xs">
            <option value="">—</option>
            {racks.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
        </label>
      )}
      <Button size="sm" onClick={onSave} disabled={saving}>Сохранить</Button>
      <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Отмена</Button>
      {error && <span className="text-red-600 dark:text-red-400">{error}</span>}
    </div>
  );
}

export default function ThresholdScheduleCard({ zoneId, racks }: { zoneId: string; racks: { id: string; name: string }[] }) {
  const [open, setOpen] = useState(false);
  const { data, mutate, isLoading } = useSWR(open && zoneId ? ['threshold-schedule', zoneId] : null, () => listThresholdSchedule(zoneId));
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startNew = () => { setDraft({ ...EMPTY, scope: 'zone' }); setError(null); setEditing('new'); };
  const startEdit = (r: ThresholdScheduleDto) => { setDraft(toDraft(r)); setError(null); setEditing(r.id); };
  const save = async () => {
    const body = toWrite(draft, zoneId);
    if (typeof body === 'string') { setError(body); return; }
    setSaving(true); setError(null);
    try {
      if (editing === 'new') await createThresholdSchedule(body);
      else if (typeof editing === 'number') await patchThresholdSchedule(editing, body);
      await mutate();
      setEditing(null);
    } catch (e: unknown) {
      const d = (e as { response?: { data?: unknown } })?.response?.data;
      setError(typeof d === 'string' ? d : d ? JSON.stringify(d) : 'Не сохранилось');
    } finally { setSaving(false); }
  };
  const remove = async (r: ThresholdScheduleDto) => {
    if (!window.confirm(`Удалить строку «${r.rack_type || 'любой тип'} ${whenLabel(r)} ×${r.multiplier}»?`)) return;
    await deleteThresholdSchedule(r.id);
    await mutate();
  };
  const toggle = async (r: ThresholdScheduleDto) => { await patchThresholdSchedule(r.id, { enabled: !r.enabled }); await mutate(); };

  const rows = data ?? [];
  return (
    <Card>
      <CardContent className="p-0">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/50">
          {open ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
          <span className="font-medium text-foreground">Вечернее понижение порога выкладки</span>
          <span className="text-xs text-muted-foreground">тип стеллажа → с часа или за N ч до закрытия → множитель порога; стеллаж › филиал › все</span>
        </button>
        {open && (
          <div className="space-y-2 border-t border-border px-3 py-3 text-sm">
            <div className="text-xs text-muted-foreground">
              Множитель 0 — алертов по типу нет вовсе (кроме «Переснять»), 0.5 — полка считается ниже нормы только ниже половины порога.
              Действует и в алертах, и в отчёте дня. На точках — после раскатки джетсонов.
            </div>
            {isLoading && <div className="text-xs text-muted-foreground">Загружаю…</div>}
            {rows.length > 0 && (
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr><th className="py-1 text-left font-medium">Тип</th><th className="text-left font-medium">Когда</th><th className="text-left font-medium">Множитель</th><th className="text-left font-medium">Область</th><th /></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr className={r.enabled ? '' : 'opacity-50'}>
                        <td className="py-1">{r.rack_type || <i>любой</i>}</td>
                        <td>{whenLabel(r)}</td>
                        <td>×{r.multiplier}</td>
                        <td>{scopeLabel(r)}</td>
                        <td className="whitespace-nowrap text-right">
                          <button type="button" className="mr-2 text-primary hover:underline" onClick={() => startEdit(r)}>править</button>
                          <button type="button" className="mr-2 text-muted-foreground hover:underline" onClick={() => void toggle(r)}>{r.enabled ? 'выключить' : 'включить'}</button>
                          <Tooltip content="Удалить"><button type="button" className="text-muted-foreground hover:text-red-600" aria-label="Удалить" onClick={() => void remove(r)}><Trash2 className="inline size-3.5" /></button></Tooltip>
                        </td>
                      </tr>
                      {editing === r.id && (
                        <tr><td colSpan={5}><RowForm draft={draft} setDraft={setDraft} racks={racks} onSave={save} onCancel={() => setEditing(null)} saving={saving} error={error} /></td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
            {!isLoading && rows.length === 0 && <div className="text-xs text-muted-foreground">Строк нет — порог весь день один.</div>}
            {editing === 'new'
              ? <RowForm draft={draft} setDraft={setDraft} racks={racks} onSave={save} onCancel={() => setEditing(null)} saving={saving} error={error} />
              : <Button size="sm" variant="outline" onClick={startNew}><Plus className="mr-1 size-3.5" />Добавить строку</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
